import { t } from 'i18next';
import { urlHelper } from 'src/app/helpers';
import { getFormData } from 'src/app/helpers/form.helper';
import {
  infiniteQueryOptions,
  InitialPageParam,
  onQueryStartedCreate,
  onQueryStartedDelete,
  onQueryStartedUpdate,
} from 'src/app/helpers/redux.helper';
import { apiSlice } from 'src/app/redux/api/apiSlice';
import { ImportDataType } from 'src/app/redux/interests/interest-slice.type';
import {
  VehicleIdentifiedCreateDataType,
  VehicleIdentifiedLookupDataType,
  VehicleIdentifiedLookupResultType,
  VehicleIdentifiedUpdateDataType,
} from 'src/app/redux/interests/vehicles/vehicle-slice.type';
import { VehicleIdentifiedDataType } from 'src/app/services/interest/Vehicle/vehicle-service.type';
import { finalUrlsConfig } from 'src/configs/url.config';
import { MutationType } from 'src/enums/operation.enum';

export const vehicleIdentifiedSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getVehicleIdentified: builder.infiniteQuery<
      VehicleIdentifiedLookupResultType,
      VehicleIdentifiedLookupDataType | void,
      InitialPageParam
    >({
      infiniteQueryOptions,
      query: ({ pageParam, queryArg }) => {
        return {
          url: finalUrlsConfig().interest.identified.vehicle.lookup,
          params: {
            ...pageParam,
            ...queryArg,
          },
        };
      },

      providesTags: [{ type: 'VehicleIdentified', id: 'LIST' }],
    }),
    getVehiclesIdentifiedAll: builder.query<VehicleIdentifiedLookupResultType, VehicleIdentifiedLookupDataType | void>({
      query: (params?: VehicleIdentifiedLookupDataType) => ({
        url: finalUrlsConfig().interest.identified.vehicle.lookup,
        params: urlHelper.getLookupQuery(params),
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.rows.map(({ id }) => ({
                type: 'VehicleIdentified' as const,
                id,
              })),
              { type: 'VehicleIdentified', id: 'LIST' },
            ]
          : [{ type: 'VehicleIdentified', id: 'LIST' }],
    }),
    createVehicle: builder.mutation<VehicleIdentifiedDataType, VehicleIdentifiedCreateDataType>({
      query: (data) => ({
        url: finalUrlsConfig().interest.identified.vehicle.create,
        method: 'POST',
        body: getFormData(data, ['pictures'], MutationType.CREATE),
      }),
      onQueryStarted: async (vehicleData, api) => onQueryStartedCreate(vehicleData, api, 'getVehicleIdentified'),
      invalidatesTags: [
        // { type: 'VehicleIdentified', id: 'LIST' },
        { type: 'Interests', id: 'LIST' },
      ],
    }),
    updateVehicle: builder.mutation<VehicleIdentifiedDataType, VehicleIdentifiedUpdateDataType>({
      query: (data) => ({
        url: finalUrlsConfig().interest.identified.vehicle.update,
        body: getFormData(data, ['pictures']),
        method: 'PATCH',
      }),
      onQueryStarted: async ({ id }, api) => onQueryStartedUpdate({ id }, api, 'getVehicleIdentified'),

      // invalidatesTags: (_result, _error, data) => [{ type: 'VehicleIdentified', id: data.id }],
    }),
    deleteVehicle: builder.mutation<void, string>({
      query: (id: string) => ({
        url: `${finalUrlsConfig().interest.identified.vehicle.delete}/${id}`,
        method: 'DELETE',
      }),
      onQueryStarted: async (id, api) => onQueryStartedDelete(id, api, 'getVehicleIdentified'),

      invalidatesTags: (_result, _error, id) => [
        // { type: 'VehicleIdentified', id },
        { type: 'Interests', id: 'LIST' },
      ],
    }),
    exportVehicleIdentifiedExcell: builder.mutation<ArrayBuffer, { ids?: string[] }>({
      query: (data) => ({
        url: finalUrlsConfig().exports.vehicle,
        method: 'POST',
        body: data,
        responseHandler: async (response: any) => {
          if (!response.ok) {
            const errorResponse = await response.json();
            throw new Error(errorResponse?.message || t('errors.defaultErrorText'));
          }
          return response.blob();
        },
        cache: 'no-cache',
      }),
    }),
    importVehicle: builder.mutation<void, ImportDataType>({
      query: (data) => ({
        url: finalUrlsConfig().interest.identified.vehicle.import,
        method: 'POST',
        body: getFormData(data, ['file']),
      }),

      invalidatesTags: [{ type: 'VehicleIdentified', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetVehiclesIdentifiedAllQuery,
  useCreateVehicleMutation,
  useUpdateVehicleMutation,
  useDeleteVehicleMutation,
  useExportVehicleIdentifiedExcellMutation,
  useLazyGetVehiclesIdentifiedAllQuery,
  useGetVehicleIdentifiedInfiniteQuery,
  useImportVehicleMutation,
} = vehicleIdentifiedSlice;
